import { useEffect, useState } from "react";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { ChangePasswordForm } from "../types";
import { useCognitoUserpoolPolicy } from "../hooks";
import { useQueryParam } from "../../_core/hooks";
import { getSensitiveParam } from "../lib/sensitiveParams";
import { useQueryClient } from "@tanstack/react-query";
import UILoader from "../../_core/components/ui/UILoader";
import CognitoPasswordChangeForm from "../components/CognitoPasswordChangeForm";

const ForcePasswordChange = () => {
  const [isSigning, setIsSigning] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { data: passwordPolicy, isFetching } = useCognitoUserpoolPolicy();
  const navigate = useNavigate();
  // Prefer the stashed value (stripped from the URL at bootstrap); fall back to the query param.
  const usernameParam = useQueryParam("username") as string;
  const passwordParam = useQueryParam("pass") as string;
  const username = getSensitiveParam("username") ?? usernameParam;
  const password = getSensitiveParam("pass") ?? passwordParam;
  const queryClient = useQueryClient();

  // Sign in user programmatically using the encoded credentials
  useEffect(() => {
    const performSignIn = async () => {
      if (!username || !password) {
        setError("Oops! Invalid link. Try requesting a new one, or reach out to support for help.");
        setIsSigning(false);
        return;
      }

      signIn({ username, password: password })
        .catch(() => {
          setError(
            `Oops! This link isn't working. It may have expired or already been used. Please request a new one or contact support for assistance. The link remains valid for ${
              passwordPolicy?.temporaryPasswordValidityDays || 7
            } days.`
          );
        })
        .finally(() => {
          setIsSigning(false);
        });
    };

    performSignIn();
     
  }, [username, password, passwordPolicy?.temporaryPasswordValidityDays]);

  async function handleChangePassword(form: ChangePasswordForm) {
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: form.password,
      });

      if (isSignedIn) {
        queryClient.clear();
        navigate("/");
      }
    } catch (error) {
      setError("Password change was not successful. Please try again or contact support if the issue persists.");
    }
  }

  useEffect(() => {
    if (error) {
      navigate("/auth/feedback", {
        state: {
          title: "Change Password",
          type: "error",
          message: error,
        },
      });
    }
  }, [error, navigate]);

  if (isSigning || isFetching) return <UILoader />;

  return <CognitoPasswordChangeForm heading="Change Password" passwordPolicy={passwordPolicy} onSubmit={handleChangePassword} />;
};

export default ForcePasswordChange;
